/**
 * ForgotPasswordPage — honest reset path.
 *
 * There is no automated reset email infrastructure today. Local-account
 * passwords are reset by a NetEng admin via the admin tools; LDAP users
 * use their directory password (Opus can't change it). This page tells
 * the user the exact path forward and how to identify themselves.
 *
 * If/when we ship an email-driven reset, replace this with a real flow
 * (form posting to /api/auth/request-reset → audit + webhook → admin
 * acts on the request).
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldAlert, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ForgotPasswordPage() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aurora-soft text-violet">
          <ShieldAlert size={22} />
        </div>
        <div>
          <h1 className="m-0 text-2xl font-extrabold tracking-tight text-fg">Can't sign in?</h1>
          <p className="mt-1 text-sm text-fg-dim">
            Password resets aren't self-service yet — here's how to get unblocked.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3 rounded-xl border border-line bg-surface-2/50 p-4">
          <Building2 size={16} className="mt-0.5 shrink-0 text-violet" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-fg">LDAP / corporate account</div>
            <p className="mt-1 text-fg-dim">
              If you normally sign in with your work email and directory
              password, Opus can't reset it for you. Reset your directory
              password through the usual corporate flow, then try signing
              in again.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-line bg-surface-2/50 p-4">
          <Mail size={16} className="mt-0.5 shrink-0 text-violet" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-fg">Local Opus account</div>
            <p className="mt-1 text-fg-dim">
              Email the NetEng team with your username and the email on
              file. An admin will set a temporary password and send it
              back. After signing in, change it from{" "}
              <span className="font-medium text-fg">Profile → Change password</span>.
            </p>
            <Button asChild size="sm" variant="ghost" className="mt-3">
              <a href="mailto:neteng@example.com?subject=Opus%20password%20reset">
                <Mail size={14} />
                Email NetEng
              </a>
            </Button>
          </div>
        </div>
      </div>

      <Button asChild variant="ghost" size="sm" className="self-start">
        <Link to="/login">
          <ArrowLeft size={14} />
          Back to sign in
        </Link>
      </Button>
    </div>
  );
}
